import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Skeleton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import Teachr from "../../../../Objects/models/Teachr";
import TableLayout from "../../../../Components/Ui/TableLayout";
import { HeadCell } from "../../../../Objects/interfaces/TableTypes";
import MlmRepository from "../../../../Repositories/MlmRepository";
import { MlmTreeNode } from "../../../../Objects/interfaces/MlmTypes";
import { useTailwindBrand } from "../../../../Components/shared";
import MlmMemberDetailDialog from "./MlmMemberDetailDialog";
import {
  flattenMlmTree,
  formatEuro,
  mlmLevelColor,
  mlmNodeFullName,
  sponsorshipStatusDisplay,
} from "./mlmUtils";

/** Libellé « septembre 2026 » → « Septembre 2026 » */
const monthLabelOf = (date: Date): string => {
  const label = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const toIsoDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * Vue "Tableau" du réseau : un mois à la fois (sélecteur ‹ Mois ›, mois courant
 * par défaut), groupée par niveau en sections repliables (seul le niveau 1 est
 * ouvert par défaut, les entêtes de groupe portent le nombre de filleuls et le
 * sous-total, rien n'est perdu). Les chiffres depuis le début vivent dans la
 * fiche détail de chaque filleul.
 */
const MlmLevelTable = ({ teachr, emptyState }: { teachr: Teachr; emptyState?: React.ReactNode }) => {
  const { brandConfig } = useTailwindBrand();
  const fontFamily = brandConfig.fontFamily;
  const primaryHex = brandConfig.colors?.primary ?? "#0dcebf";

  const [expandedLevels, setExpandedLevels] = useState<Set<number>>(new Set([1]));
  const [selectedNode, setSelectedNode] = useState<MlmTreeNode | null>(null);

  // Mois affiché : premier jour du mois sélectionné (mois courant par défaut).
  const now = new Date();
  const [monthDate, setMonthDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
  const isCurrentMonth = monthDate.getFullYear() === now.getFullYear() && monthDate.getMonth() === now.getMonth();
  const monthLabel = monthLabelOf(monthDate);
  const shiftMonth = (delta: number) =>
    setMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));

  const { data: treeData, isLoading, isError } = useQuery({
    queryKey: ["mlm", "tree", teachr?.id],
    queryFn: () => MlmRepository.getTree(teachr.id as number),
    enabled: !!teachr?.id,
  });

  // Mois passé : agrégats par filleul calculés côté SQL sur la période du mois
  // (rattachés à la date du cours). Le mois courant vient déjà des stats de l'arbre.
  const { data: monthData, isLoading: monthLoading } = useQuery({
    queryKey: ["mlm", "commissions-month", teachr?.id, monthDate.getFullYear(), monthDate.getMonth()],
    queryFn: () =>
      MlmRepository.getCommissions(teachr.id as number, {
        page: 0,
        limit: 1,
        from: toIsoDay(monthDate),
        to: toIsoDay(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0)),
      }),
    enabled: !!teachr?.id && !isCurrentMonth,
  });

  const monthTotalsBySource = useMemo(() => {
    const totals: Record<number, number> = {};
    monthData?.totalsBySource?.forEach((entry) => {
      totals[entry.sourceTeachrId] = entry.total;
    });
    return totals;
  }, [monthData]);

  const commissionOf = (node: MlmTreeNode): number =>
    isCurrentMonth ? node.stats.commissionMonth : monthTotalsBySource[node.teachr.id] ?? 0;

  const monthValueLoading = !isCurrentMonth && monthLoading;

  const headCells: HeadCell[] = [
    { id: 0, label: "Filleul", align: "left", disable: false, numeric: false },
    { id: 1, label: "Statut", align: "center", disable: false, numeric: false },
    { id: 2, label: `Commission · ${monthLabel}`, align: "center", disable: false, numeric: true },
    { id: 3, label: "", align: "center", disable: false, numeric: false },
  ];

  // Filleuls groupés par niveau, triés par commission du mois affiché
  // décroissante (les plus contributifs d'abord) puis par nom.
  const byLevel = useMemo(() => {
    const groups = new Map<number, MlmTreeNode[]>();
    if (!treeData?.tree) return groups;
    const valueOf = (node: MlmTreeNode) =>
      isCurrentMonth ? node.stats.commissionMonth : monthTotalsBySource[node.teachr.id] ?? 0;
    flattenMlmTree(treeData.tree).forEach((node) => {
      const list = groups.get(node.level) ?? [];
      list.push(node);
      groups.set(node.level, list);
    });
    groups.forEach((list) =>
      list.sort(
        (a, b) => valueOf(b) - valueOf(a) || mlmNodeFullName(a).localeCompare(mlmNodeFullName(b))
      )
    );
    return groups;
  }, [treeData, isCurrentMonth, monthTotalsBySource]);

  const levels = Array.from(byLevel.keys()).sort((a, b) => a - b);
  const totalRows = levels.reduce((sum, level) => sum + (byLevel.get(level)?.length ?? 0), 0);
  const monthTotal = levels.reduce(
    (sum, level) => sum + (byLevel.get(level) ?? []).reduce((s, node) => s + commissionOf(node), 0),
    0
  );

  const toggleLevel = (level: number) =>
    setExpandedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });

  if (isError) {
    return <Alert severity="error">Impossible de charger vos commissions. Veuillez réessayer plus tard.</Alert>;
  }

  const networkIsEmpty = !isLoading && !!treeData?.tree && totalRows === 0;
  if (networkIsEmpty && emptyState) {
    return <>{emptyState}</>;
  }

  const renderRows = () => {
    const rendered: React.ReactNode[] = [];

    levels.forEach((level) => {
      const nodes = byLevel.get(level) ?? [];
      const color = mlmLevelColor(level);
      const isExpanded = expandedLevels.has(level);
      const subtotal = nodes.reduce((sum, node) => sum + commissionOf(node), 0);

      // Entête de niveau : repliable, avec l'essentiel visible même replié.
      rendered.push(
        <TableRow
          key={`level-${level}`}
          onClick={() => toggleLevel(level)}
          sx={{ bgcolor: `${color}14`, cursor: "pointer", "&:hover": { bgcolor: `${color}24` } }}
        >
          <TableCell colSpan={2} sx={{ borderLeft: `4px solid ${color}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton size="small" aria-label={isExpanded ? `Replier le niveau ${level}` : `Déplier le niveau ${level}`}>
                {isExpanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
              </IconButton>
              <Typography variant="body2" fontWeight={700} sx={{ fontFamily, color }}>
                Niveau {level} · {treeData?.rates?.[level] ?? "?"} %
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                {nodes.length} filleul{nodes.length > 1 ? "s" : ""}
              </Typography>
            </Box>
          </TableCell>
          <TableCell align="center" sx={{ fontWeight: 700, fontFamily, color }}>
            {monthValueLoading ? <Skeleton width={60} sx={{ mx: "auto" }} /> : formatEuro(subtotal)}
          </TableCell>
          <TableCell />
        </TableRow>
      );

      if (!isExpanded) return;

      nodes.forEach((node) => {
        const statusDisplay = sponsorshipStatusDisplay(node.statut);
        rendered.push(
          <TableRow key={node.teachr.id} hover sx={{ cursor: "pointer" }} onClick={() => setSelectedNode(node)}>
            <TableCell sx={{ fontFamily }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontFamily }}>
                {mlmNodeFullName(node)}
              </Typography>
              {node.teachr.invitationCode && (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                  {node.teachr.invitationCode}
                </Typography>
              )}
            </TableCell>
            <TableCell align="center">
              {statusDisplay ? (
                <Chip size="small" variant="outlined" label={statusDisplay.title} className={statusDisplay.color} />
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell align="center" sx={{ fontFamily, fontWeight: 600 }}>
              {monthValueLoading ? <Skeleton width={60} sx={{ mx: "auto" }} /> : formatEuro(commissionOf(node))}
            </TableCell>
            <TableCell align="center">
              <Tooltip title="Voir le détail (commissions depuis le début, cours, CA)">
                <IconButton
                  size="small"
                  aria-label={`Voir le détail de ${mlmNodeFullName(node)}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedNode(node);
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        );
      });
    });

    // Total du mois affiché, tous niveaux confondus.
    rendered.push(
      <TableRow key="month-total" sx={{ bgcolor: "grey.100" }}>
        <TableCell colSpan={2} sx={{ fontWeight: 700, fontFamily }}>
          Total · {monthLabel} ({totalRows} filleul{totalRows > 1 ? "s" : ""})
        </TableCell>
        <TableCell align="center" sx={{ fontWeight: 700, fontFamily }}>
          {monthValueLoading ? <Skeleton width={60} sx={{ mx: "auto" }} /> : formatEuro(monthTotal)}
        </TableCell>
        <TableCell />
      </TableRow>
    );

    return rendered;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
          Commissions par niveau pour le mois affiché. Cliquez sur un filleul pour voir le détail depuis le début.
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 2,
            border: "1px solid",
            borderColor: `${primaryHex}50`,
            bgcolor: `${primaryHex}0d`,
          }}
        >
          <IconButton size="small" aria-label="Mois précédent" onClick={() => shiftMonth(-1)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" fontWeight={600} sx={{ fontFamily, minWidth: 130, textAlign: "center", color: primaryHex }}>
            {monthLabel}
          </Typography>
          <Tooltip title={isCurrentMonth ? "Vous êtes sur le mois en cours" : "Mois suivant"}>
            <span>
              <IconButton size="small" aria-label="Mois suivant" onClick={() => shiftMonth(1)} disabled={isCurrentMonth}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {isLoading ? (
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      ) : totalRows === 0 ? (
        <Alert severity="info" sx={{ fontFamily }}>
          Votre réseau est vide pour le moment. Partagez votre code de parrainage pour développer votre équipe.
        </Alert>
      ) : (
        <TableLayout
          headCells={headCells}
          page={1}
          rowsPerPage={totalRows}
          count={totalRows}
          handleChangePage={() => undefined}
          handleChangeRowsPerPage={() => undefined}
          hidePagination
          size="small"
          stickyHeader
          paperSx={{ boxShadow: 2, borderRadius: 2 }}
          Children={<>{renderRows()}</>}
        />
      )}

      <MlmMemberDetailDialog node={selectedNode} open={selectedNode !== null} onClose={() => setSelectedNode(null)} />
    </Box>
  );
};

export default MlmLevelTable;
